/**
 * The display-once registration-token block (design §10.2 step B).
 *
 * The full token exists client-side exactly once — in the MintedClaim the
 * create call returned; the claims list endpoint masks it server-side. This
 * panel is therefore the admin's ONLY chance to copy it, and says so.
 */

import { useState } from 'react';
import { Check, Copy, KeyRound, ShieldCheck } from 'lucide-react';
import type { MintedClaim } from '../../services/access.service';

const CopyButton = ({ text, label }: { text: string; label: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be unavailable (http origin) — the text is selectable.
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-600 hover:border-primary-400 hover:text-primary-700"
      title={`Copy ${label}`}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-success-600" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

function daysUntil(iso: string): number {
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000));
}

interface ClaimTokenPanelProps {
  claim: MintedClaim;
  kind: 'gateway' | 'app_connector';
  /** The node's display name, for the heading. */
  nodeName: string;
  /**
   * The registered node_id — the CN the bootstrap's CSR must carry, so it is
   * part of the install command. Enrolment refuses a CN that does not match.
   */
  nodeId: string;
}

const ClaimTokenPanel = ({ claim, kind, nodeName, nodeId }: ClaimTokenPanelProps) => {
  const role = kind === 'gateway' ? 'gateway' : 'connector';
  const orchestratorUrl = window.location.origin;
  const snippet = [
    `securelink-access enroll \\`,
    `    --orchestrator ${orchestratorUrl} \\`,
    `    --role ${role} \\`,
    `    --node-id ${nodeId} \\`,
    `    --token ${claim.token}`,
  ].join('\n');
  const days = daysUntil(claim.expiresAt);

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-800">
        This token is shown <span className="font-semibold">once</span> and cannot be retrieved later —
        store it securely before closing. It is single-use and expires in {days} day{days === 1 ? '' : 's'}.
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
            <KeyRound className="h-4 w-4 text-teal-600" /> Registration token
          </span>
          <CopyButton text={claim.token} label="token" />
        </div>
        <code className="block select-all rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-base tracking-wide text-gray-900">
          {claim.token}
        </code>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Run on the {role} host</span>
          <CopyButton text={snippet} label="command" />
        </div>
        <pre className="overflow-x-auto rounded-md border border-gray-200 bg-gray-900 px-3 py-2 font-mono text-xs leading-5 text-gray-100">
          {snippet}
        </pre>
      </div>

      <p className="flex items-start gap-2 text-xs text-gray-500">
        <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-teal-600" />
        <span>
          <span className="font-medium text-gray-600">{nodeName}</span> generates its keypair locally and
          submits a CSR with CN <code className="text-gray-600">{nodeId}</code> — its identity from then on;
          the orchestrator returns its signed device certificate. The private key never leaves the node.
          Once enrolled, the row flips from <em>awaiting enrolment</em> to its live status.
        </span>
      </p>
    </div>
  );
};

export default ClaimTokenPanel;
